import React, { useEffect, useState } from 'react';

import { ListFieldSection, PageSection, GridFieldSection } from '../../Layout';
import { Field, NumericInput, TextOutput, DateInput, TimeInput, Checkbox, TextInput, RadioGroup } from '../../Core';
import { ChangeEventWithData } from 'Components/Core/types';
import { IWithWorksheetInjectedProps, WorksheetRecordGrid } from 'Components/Worksheets/Core';
import { toWordsOrdinal } from 'number-to-words';
import { withErrorBoundary } from 'Components/Core/ErrorBoundary';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { useSelector } from 'react-redux';
import { pipe } from 'Lib/Hooks/Selectors';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import { joinPaths } from 'Lib/Object/Path/shared';
import { useHasChanged, usePrevious } from 'Lib/Hooks';
import { DateTimeDataFormat } from 'Lib/Format';

type LabOption = 'always' | 'optional' | 'never';

let DryMassEntryGridSectionTest: React.FC<IWithWorksheetInjectedProps> = ({
  createWorksheetSelector,
  addArrayEntry,
  removeArrayEntry,
}) => {
  const [
    gridOrientation,
    //, setGridOrientation
  ] = useState('horizontal');
  const [syncingReadings, setSyncingReadings] = useState(false);
  const [syncLatest, setSyncLatest] = useState(false);
  const [labOption, setLabOption] = useState<LabOption>('optional');
  const [requireDryMassEntryBeforeAdding, setRequireDryMassEntryBeforeAdding] = useState(false);

  // TODO: Make it so we can get the grid to work this out from the records instead of selecting in the parent
  const lastDryMassEnteredArray = useSelector(
    pipe(
      createWorksheetSelector!('MoistureDeterminations'),
      (determination) =>
        determination
          ?.map((r: Array<any>) => r?.['DryMassReadings' as any] as any)
          ?.map((r: any) => (hasNonEmptyValue(r) ? hasValue(r[r.length - 1]?.['TareAndMaterialDryMass']) : false)) ?? []
    ),
    deepEquals
  );

  // Ensure at least one/two readings exist for each determination
  // (All this garbage is here to mock back-end syncing)
  const optionChanged = useHasChanged(labOption);
  const lengthIncreased = usePrevious(lastDryMassEnteredArray.length) < lastDryMassEnteredArray.length;
  const targetReadings = labOption === 'always' ? 2 : 1;

  useEffect(() => {
    if (optionChanged) setSyncingReadings(true);
  }, [optionChanged]);

  useEffect(() => {
    if (lengthIncreased) setSyncLatest(true);
  }, [lengthIncreased]);

  const dryMassReadingCountArray = useSelector(
    pipe(createWorksheetSelector!('MoistureDeterminations'), (determination) =>
      determination?.map((r: Array<any>) => r?.['DryMassReadings' as any]?.length ?? 0)
    ),
    deepEquals
  );

  useEffect(() => {
    if (syncingReadings) {
      let moreToBeDone = false;
      dryMassReadingCountArray?.forEach((e: number, i: number) => {
        if (e < targetReadings) {
          moreToBeDone = true;
          addArrayEntry!({
            name: joinPaths('MoistureDeterminations', `${i}`, 'DryMassReadings'),
            index: e ?? 0,
            sync: true,
            data: undefined,
          });
        } else if (e > targetReadings) {
          moreToBeDone = true;
          removeArrayEntry!({
            name: joinPaths('MoistureDeterminations', `${i}`, 'DryMassReadings'),
            index: e - 1,
            sync: true,
            data: undefined,
          });
        }
      });
      if (!moreToBeDone) setSyncingReadings(false);
    }
  }, [syncingReadings, targetReadings, dryMassReadingCountArray, addArrayEntry, removeArrayEntry]);

  useEffect(() => {
    if (syncLatest) {
      if (dryMassReadingCountArray[dryMassReadingCountArray.length - 1] < targetReadings) {
        addArrayEntry!({
          name: joinPaths('MoistureDeterminations', `${dryMassReadingCountArray.length - 1}`, 'DryMassReadings'),
          index: dryMassReadingCountArray[dryMassReadingCountArray.length - 1],
          sync: true,
          data: undefined,
        });
      } else if (dryMassReadingCountArray[dryMassReadingCountArray.length - 1] > targetReadings) {
        removeArrayEntry!({
          name: joinPaths('MoistureDeterminations', `${dryMassReadingCountArray.length - 1}`, 'DryMassReadings'),
          index: dryMassReadingCountArray[dryMassReadingCountArray.length - 1] - 1,
          sync: true,
          data: undefined,
        });
      } else {
        setSyncLatest(false);
      }
    }
  }, [syncLatest, targetReadings, dryMassReadingCountArray, addArrayEntry, removeArrayEntry]);

  return (
    <>
      <PageSection heading="Options">
        <ListFieldSection>
          <Field
            label="Laboratory Option: Enter Drying to Constant Mass"
            input={<RadioGroup />}
            options={[
              { label: 'Always', value: 'always' },
              { label: 'Optional', value: 'optional' },
              { label: 'Never', value: 'never' },
            ]}
            value={labOption}
            onChange={(e: ChangeEventWithData) => setLabOption(e.data?.value)}
          />
          <Field
            label="Require Last Dry Mass to be Entered to Add New Entry"
            input={<Checkbox />}
            value={requireDryMassEntryBeforeAdding}
            onChange={(e: ChangeEventWithData) => setRequireDryMassEntryBeforeAdding(e.data?.value)}
            disabled={labOption === 'never'}
          />
          {/* <Field
            label="Grid Orientation"
            input={<RadioGroup />}
            options={[
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
            ]}
            value={gridOrientation}
            onChange={(e: ChangeEventWithData) => setGridOrientation(e.data?.value)}
          /> */}
        </ListFieldSection>
      </PageSection>
      <PageSection heading="Dry Mass Entry - Moisture Content in Grid">
        <GridFieldSection>
          <WorksheetRecordGrid
            orientation={gridOrientation as 'horizontal' | 'vertical'}
            name={'MoistureDeterminations'}
            recordProps={{
              preventRemove: 'all-but-last',
              enforceAtLeastOneRecord: true,
            }}
            fieldSet={[
              {
                name: 'ContainerLabel',
                component: <Field input={<TextInput />} />,
              },
              {
                name: 'ContainerMass',
                component: <Field input={<NumericInput />} />,
              },
              {
                name: 'TareAndMaterialWetMass',
                component: <Field input={<NumericInput />} />,
              },
              ...(labOption !== 'never'
                ? [
                    {
                      name: 'DateTimeInOven',
                      component: (
                        <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                      ),
                      label: 'Date in Oven',
                    },
                    {
                      name: 'DateTimeInOven',
                      component: (
                        <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                      ),
                      label: 'Time in Oven',
                    },
                  ]
                : []),
              {
                name: 'DryMassReadings',
                recordProps: (i: number) => ({
                  heading:
                    labOption !== 'never'
                      ? (i: number) => (i > 0 ? `${toWordsOrdinal(i + 1)} Drying` : 'Drying')
                      : undefined,
                  enforceAtLeastOneRecord: true,
                  preventRemove: 'all-but-last',
                  preventCreate:
                    labOption === 'never' || (requireDryMassEntryBeforeAdding && !lastDryMassEnteredArray[i]),
                  removeConfirm: {
                    confirm: true,
                    confirmTitle: 'Delete Dry Mass Reading',
                    confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                    confirmButtonText: 'Delete',
                  },
                }),
                fields: [
                  ...(labOption !== 'never'
                    ? [
                        {
                          name: 'DateTimeOutOfOven',
                          component: (
                            <Field input={<DateInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                          ),
                          label: 'Date Out of Oven',
                        },
                        {
                          name: 'DateTimeOutOfOven',
                          component: (
                            <Field input={<TimeInput />} dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime} />
                          ),
                          label: 'Time Out of Oven',
                        },
                        {
                          name: 'DryingTime',
                          component: <Field input={<TextOutput />} value={'Calculated'} />,
                        },
                      ]
                    : []),
                  {
                    name: 'TareAndMaterialDryMass',
                    component: <Field input={<NumericInput />} />,
                  },
                ],
              },
              {
                name: 'MoistureContent',
                component: <Field input={<TextOutput />} value={'Calculated'} />,
              },
            ]}
          />
        </GridFieldSection>
      </PageSection>
    </>
  );
};

DryMassEntryGridSectionTest = withErrorBoundary(withInjectedProps(DryMassEntryGridSectionTest));

export default DryMassEntryGridSectionTest;
export { DryMassEntryGridSectionTest };
